import { z } from 'zod';
import { LeadStatusSchema } from '../lead/leads.schemas';
import { PropertyStatusSchema } from '../property/properties.schemas';
import { DealPhaseSchema, DealStatusSchema, DealTypeSchema } from './deals.schemas';

/**
 * The operator's Standort record move (IMM-1104). An operator selects deals,
 * Objekte, Kontakte and Leads of one Standort and moves them to another Standort
 * of the same workspace. The server expands the selection one relation downward
 * (a deal brings its Objekt and Kontakte, a Lead its Kontakt and Objekt, an
 * Objekt its Kontakte); see `OrganizationRecordMovePlanSchema`.
 */
export const ORGANIZATION_RECORD_KINDS = ['DEAL', 'PROPERTY', 'CONTACT', 'LEAD'] as const;
export const OrganizationRecordKindSchema = z.enum(ORGANIZATION_RECORD_KINDS);
export type OrganizationRecordKind = z.infer<typeof OrganizationRecordKindSchema>;

export const MAX_ORGANIZATION_RECORD_SELECTION = 200;

export const AdminOrganizationRecordsPathParamsSchema = z.object({ id: z.uuid() });

export const OrganizationDealRecordSchema = z.object({
  id: z.uuid(),
  type: DealTypeSchema,
  phase: DealPhaseSchema,
  status: DealStatusSchema,
  propertyId: z.uuid(),
  // The Objekt-Nr. and address of the deal's Objekt; null when the Objekt is deleted.
  propertyObjectNumber: z.string().nullable(),
  propertyLabel: z.string().nullable(),
  responsibleMemberNames: z.array(z.string()),
  updatedAt: z.iso.datetime(),
});

export const OrganizationPropertyRecordSchema = z.object({
  id: z.uuid(),
  objectNumber: z.string(),
  title: z.string(),
  formattedAddress: z.string().nullable(),
  status: PropertyStatusSchema,
  updatedAt: z.iso.datetime(),
});

export const OrganizationContactRecordSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  contactType: z.enum(['PERSON', 'COMPANY']),
  updatedAt: z.iso.datetime(),
});

export const OrganizationLeadRecordSchema = z.object({
  id: z.uuid(),
  ownerName: z.string(),
  status: LeadStatusSchema,
  receivedAt: z.iso.datetime(),
});

export const OrganizationMoveTargetSchema = z.object({ id: z.uuid(), name: z.string() });

export const ListOrganizationRecordsResponseSchema = z.object({
  deals: z.array(OrganizationDealRecordSchema),
  properties: z.array(OrganizationPropertyRecordSchema),
  contacts: z.array(OrganizationContactRecordSchema),
  leads: z.array(OrganizationLeadRecordSchema),
  // The ACTIVE Standorte of the same workspace, this one excluded — where the records may move to.
  targets: z.array(OrganizationMoveTargetSchema),
});

const uniqueIds = z
  .array(z.uuid())
  .max(MAX_ORGANIZATION_RECORD_SELECTION)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'ids must be unique' });

export const OrganizationRecordIdsSchema = z.object({
  deals: uniqueIds.default([]),
  properties: uniqueIds.default([]),
  contacts: uniqueIds.default([]),
  leads: uniqueIds.default([]),
});

const selectionSize = (ids: z.infer<typeof OrganizationRecordIdsSchema>): number =>
  ids.deals.length + ids.properties.length + ids.contacts.length + ids.leads.length;

const requireSelection = (selection: z.infer<typeof OrganizationRecordIdsSchema>, context: z.RefinementCtx): void => {
  const size = selectionSize(selection);

  if (size === 0) {
    context.addIssue({ code: 'custom', path: ['selection'], message: 'select at least one record' });
  }

  if (size > MAX_ORGANIZATION_RECORD_SELECTION) {
    context.addIssue({
      code: 'custom',
      path: ['selection'],
      message: `select at most ${MAX_ORGANIZATION_RECORD_SELECTION} records`,
    });
  }
};

export const PreviewOrganizationRecordMoveBodySchema = z
  .object({ targetOrganizationId: z.uuid(), selection: OrganizationRecordIdsSchema })
  .superRefine((body, context) => requireSelection(body.selection, context));

// The responsible-member change of one moving deal or Lead, by member id — what the
// operator confirmed, sent back so the server can tell whether it still holds.
export const OrganizationRecordResponsibilityIdsSchema = z.object({
  kind: z.enum(['DEAL', 'LEAD']),
  id: z.uuid(),
  kept: z.array(z.uuid()),
  removed: z.array(z.uuid()),
  assigned: z.array(z.uuid()),
});

export const MoveOrganizationRecordsBodySchema = z
  .object({
    targetOrganizationId: z.uuid(),
    selection: OrganizationRecordIdsSchema,
    // The `moving` id set the preview answered and the operator confirmed. The
    // server plans the move again and refuses it when the set differs.
    expected: OrganizationRecordIdsSchema,
    // The preview's `responsibility`, by member id. Refused the same way when the
    // responsible members the move would keep, remove or assign changed since.
    // Only selected deals and Leads move as deals and Leads, so it is bounded by the selection.
    expectedResponsibility: z.array(OrganizationRecordResponsibilityIdsSchema).max(MAX_ORGANIZATION_RECORD_SELECTION),
  })
  .superRefine((body, context) => requireSelection(body.selection, context));

// How a record reached by the move is related to the selected record that brought it.
export const ORGANIZATION_RECORD_RELATIONS = [
  'DEAL_PROPERTY',
  'DEAL_CONTACT',
  'PROPERTY_CONTACT',
  'LEAD_CONTACT',
  'LEAD_PROPERTY',
] as const;
export const OrganizationRecordRelationSchema = z.enum(ORGANIZATION_RECORD_RELATIONS);
export type OrganizationRecordRelation = z.infer<typeof OrganizationRecordRelationSchema>;

export const OrganizationRecordRefSchema = z.object({
  kind: OrganizationRecordKindSchema,
  id: z.uuid(),
  // Objekt-Nr. and address for an Objekt or a deal (its Objekt's), the name for a Kontakt or a Lead.
  label: z.string(),
});

const MovingOrganizationRecordSchema = OrganizationRecordRefSchema.extend({
  // Records of the source Standort that stay there and stay linked to this one: the
  // source Standort keeps access to it through the link (IMM-1105). Empty when none.
  staysLinkedTo: z.array(OrganizationRecordRefSchema),
});

export const IncludedOrganizationRecordSchema = MovingOrganizationRecordSchema.extend({
  // The selected records that brought it in, each with the relation.
  via: z.array(z.object({ record: OrganizationRecordRefSchema, relation: OrganizationRecordRelationSchema })),
});

export const OrganizationRecordResponsibleMemberSchema = z.object({
  memberId: z.uuid(),
  // Null when the member is no longer an active member of the workspace.
  displayName: z.string().nullable(),
});

// Why the move assigns new responsible members: the target Standort's active
// Standortleitungen, or the workspace Owner when it has none — the rule new Leads use.
export const ORGANIZATION_RECORD_RESPONSIBILITY_ASSIGNMENTS = ['LOCATION_MANAGER', 'OWNER_FALLBACK'] as const;
export const OrganizationRecordResponsibilityAssignmentSchema = z.enum(ORGANIZATION_RECORD_RESPONSIBILITY_ASSIGNMENTS);

// What a move does to one moving deal's or Lead's responsible members (IMM-1104).
// Members who see the target Standort stay responsible (`kept`); every other one is
// removed. When nobody stays, the target's Standortleitungen — or the workspace
// Owner — become responsible (`assigned`, with `assignedVia`).
export const OrganizationRecordResponsibilityChangeSchema = z.object({
  record: OrganizationRecordRefSchema,
  kept: z.array(OrganizationRecordResponsibleMemberSchema),
  removed: z.array(OrganizationRecordResponsibleMemberSchema),
  assigned: z.array(OrganizationRecordResponsibleMemberSchema),
  assignedVia: OrganizationRecordResponsibilityAssignmentSchema.nullable(),
});

export const OrganizationRecordMovePlanSchema = z.object({
  sourceOrganizationId: z.uuid(),
  targetOrganizationId: z.uuid(),
  selected: z.array(MovingOrganizationRecordSchema),
  included: z.array(IncludedOrganizationRecordSchema),
  // Selected plus included: the id set the move writes, and the `expected` value to send back.
  moving: z.object({
    deals: z.array(z.uuid()),
    properties: z.array(z.uuid()),
    contacts: z.array(z.uuid()),
    leads: z.array(z.uuid()),
  }),
  // One entry per moving deal and Lead, deals first.
  responsibility: z.array(OrganizationRecordResponsibilityChangeSchema),
});

export type OrganizationDealRecord = z.infer<typeof OrganizationDealRecordSchema>;
export type OrganizationPropertyRecord = z.infer<typeof OrganizationPropertyRecordSchema>;
export type OrganizationContactRecord = z.infer<typeof OrganizationContactRecordSchema>;
export type OrganizationLeadRecord = z.infer<typeof OrganizationLeadRecordSchema>;
export type OrganizationMoveTarget = z.infer<typeof OrganizationMoveTargetSchema>;
export type ListOrganizationRecordsResponse = z.infer<typeof ListOrganizationRecordsResponseSchema>;
export type OrganizationRecordIds = z.infer<typeof OrganizationRecordIdsSchema>;
export type PreviewOrganizationRecordMoveBody = z.input<typeof PreviewOrganizationRecordMoveBodySchema>;
export type MoveOrganizationRecordsBody = z.input<typeof MoveOrganizationRecordsBodySchema>;
export type OrganizationRecordRef = z.infer<typeof OrganizationRecordRefSchema>;
export type IncludedOrganizationRecord = z.infer<typeof IncludedOrganizationRecordSchema>;
export type OrganizationRecordMovePlan = z.infer<typeof OrganizationRecordMovePlanSchema>;
export type OrganizationRecordResponsibleMember = z.infer<typeof OrganizationRecordResponsibleMemberSchema>;
export type OrganizationRecordResponsibilityAssignment = z.infer<
  typeof OrganizationRecordResponsibilityAssignmentSchema
>;
export type OrganizationRecordResponsibilityChange = z.infer<typeof OrganizationRecordResponsibilityChangeSchema>;
export type OrganizationRecordResponsibilityIds = z.infer<typeof OrganizationRecordResponsibilityIdsSchema>;
